import {
    GHGEmissionsReportSettings,
    useReportsApi,
    useSustainabilityApi,
} from '@/hooks'
import useToastStore from '@/stores/useToastStore/useToastStore'
import {
    DEFAULT_GHG_EMISSIONS_REPORT_SETTINGS,
    DEFAULT_SUSTAINABILITY_FILTERING,
} from '@/utils/constants'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { processSustainabilityFilters } from '../utils/processSustainabilityFilters'
import { mapSustainabilityParamsToFilters } from '@/features/Sustainability/utils/mapSustainabilityParamsToFilters'
import { useGHGEmissionsFiltersGetParams } from '@/features/Sustainability/hooks/useGHGEmissionsFiltersGetParams'

export const useExportGHGEmissionsReport = (reportId: string) => {
    const addAlert = useToastStore((state) => state.addAlert)
    const { t } = useTranslation('translations')
    const triggered = useRef(false)

    const { useGetReport } = useReportsApi()
    const { data: report, refetch: refetchReport } = useGetReport(reportId, {
        enabled: false,
    })

    const { useGHGEmissionsQuery } = useSustainabilityApi()

    const reportSettings = useMemo((): GHGEmissionsReportSettings => {
        if (
            report?.settings &&
            typeof report.settings === 'object' &&
            'scope' in report.settings &&
            'showTotals' in report.settings
        ) {
            return report.settings
        }
        return DEFAULT_GHG_EMISSIONS_REPORT_SETTINGS
    }, [report?.settings])

    const sustainabilityFilters = useMemo(() => {
        if (!report?.filters) {
            return DEFAULT_SUSTAINABILITY_FILTERING
        }

        const mergedFilters = {
            ...report.filters,
            scope: reportSettings.scope,
            scope2type: reportSettings.scope2type,
            showTotals: reportSettings.showTotals,
        }

        const processedParams = processSustainabilityFilters(mergedFilters)

        return mapSustainabilityParamsToFilters(processedParams)
    }, [report?.filters, reportSettings])

    const processedFilters = useGHGEmissionsFiltersGetParams(
        sustainabilityFilters,
        reportSettings
    )

    const {
        data: ghgEmissions,
        refetch: refetchGHGEmissions,
        downloadXlsx,
    } = useGHGEmissionsQuery(processedFilters, {
        enabled: !!report && triggered.current,
    })

    const downloadData = useCallback(() => {
        downloadXlsx().catch(() => {
            addAlert({
                variant: 'error',
                title: t('reporting.export_report.error_toast.title'),
                description: t(
                    'reporting.export_report.error_toast.description'
                ),
            })
        })
    }, [addAlert, downloadXlsx, t])

    useEffect(() => {
        if (triggered.current) {
            triggered.current = false
            downloadData()
        }
    }, [downloadData, ghgEmissions])

    const exportGHGEmissionsReport = () => {
        triggered.current = true

        if (!report) {
            void refetchReport()
            return
        }

        if (!ghgEmissions) {
            void refetchGHGEmissions()
            return
        }

        triggered.current = false
        downloadData()
    }

    return {
        exportGHGEmissionsReport,
    }
}
